import { Component } from '@theme/component';
import { ThemeEvents, ZoomMediaSelectedEvent } from '@theme/events';
import { StandardEvents, ProductSelectEvent } from '@shopify/events';

/**
 * A custom element that renders a media gallery.
 *
 * @typedef {object} Refs
 * @property {import('./zoom-dialog').ZoomDialog} [zoomDialogComponent] - The zoom dialog component.
 * @property {import('./slideshow').Slideshow} [slideshow] - The slideshow component.
 * @property {HTMLElement[]} [media] - The media elements.
 *
 * @extends Component<Refs>
 */
export class MediaGallery extends Component {
  connectedCallback() {
    super.connectedCallback();

    const { signal } = this.#controller;
    this.#startImageGate(signal);
    const target = this.closest('.shopify-section, dialog');

    target?.addEventListener(StandardEvents.productSelect, this.#handleProductSelect, { signal });
    this.refs.zoomDialogComponent?.addEventListener(ThemeEvents.zoomMediaSelected, this.#handleZoomMediaSelected, {
      signal,
    });
  }

  /**
   * LUX patch B3 (gallery rev3, ported from live 2026-09-11): releases mobile-deferred gallery images
   * (snippets/product-media.liquid) after the primary image paints, on interaction, on desktop, or after 15 s.
   *
   * @param {AbortSignal} signal
   */
  #startImageGate(signal) {
    const images = Array.from(this.querySelectorAll('img[data-lux-gallery-image]'));
    if (!images.length) return;

    let released = false;
    let fallback;
    const release = () => {
      if (released || !this.isConnected) return;
      released = true;
      clearTimeout(fallback);
      for (const image of images) {
        const srcset = image.getAttribute('data-lux-gallery-srcset');
        const src = image.getAttribute('data-lux-gallery-src');
        if (srcset) image.setAttribute('srcset', srcset);
        if (src) image.setAttribute('src', src);
        image.removeAttribute('data-lux-gallery-srcset');
        image.removeAttribute('data-lux-gallery-src');
        image.removeAttribute('data-lux-gallery-image');
        image.closest('picture[data-lux-gallery-picture]')?.replaceWith(image);
      }
    };
    const desktop = matchMedia('(min-width: 750px)');
    if (desktop.matches) {
      release();
      return;
    }

    for (const type of ['pointerdown', 'keydown', 'click']) {
      this.addEventListener(type, release, { capture: true, once: true, signal });
    }
    desktop.addEventListener('change', (event) => { if (event.matches) release(); }, { signal });
    signal.addEventListener('abort', () => clearTimeout(fallback), { once: true });

    const primary = this.querySelector('.product-media__image:not([data-lux-gallery-image])');
    if (!primary || (primary.complete && primary.naturalWidth === 0)) {
      release();
      return;
    }
    const afterPaint = () => requestAnimationFrame(() => requestAnimationFrame(release));
    if (primary.complete && primary.naturalWidth > 0) {
      afterPaint();
    } else {
      primary.addEventListener('load', afterPaint, { once: true, signal });
      primary.addEventListener('error', release, { once: true, signal });
    }
    fallback = setTimeout(release, 15000);
  }

  #controller = new AbortController();

  disconnectedCallback() {
    super.disconnectedCallback();

    this.#controller.abort();
  }

  /**
   * Handles a product select event by replacing the current media gallery with a new one.
   *
   * @param {ProductSelectEvent} event - The product select event.
   */
  #handleProductSelect = (event) => {
    if (!(event.target instanceof Element) || event.target.closest('product-card')) return;

    event.promise
      .then(({ detail }) => {
        if (!detail?.html) return;

        const { html } = detail;
        const newMediaGallery = html.querySelector('media-gallery');
        if (!newMediaGallery) return;

        this.replaceWith(newMediaGallery);
      })
      .catch((error) => {
        if (error?.name !== 'AbortError') console.warn('[media-gallery] Event promise rejected:', error);
      });
  };

  /**
   * Handles the 'zoom-media:selected' event.
   * @param {ZoomMediaSelectedEvent} event - The zoom-media:selected event.
   */
  #handleZoomMediaSelected = async (event) => {
    this.slideshow?.select(event.detail.index, undefined, { animate: false });
  };

  /**
   * Zooms the media gallery.
   *
   * @param {number} index - The index of the media to zoom.
   * @param {PointerEvent} event - The pointer event.
   */
  zoom(index, event) {
    this.refs.zoomDialogComponent?.open(index, event);
  }

  /**
   * Preloads an image.
   * @param {number} index - The index of the media to preload.
   */
  preloadImage(index) {
    const zoomDialogMedia = this.refs.zoomDialogComponent?.refs.media[index];
    if (!zoomDialogMedia) return;

    this.refs.zoomDialogComponent?.loadHighResolutionImage(zoomDialogMedia);
  }

  get slideshow() {
    return this.refs.slideshow;
  }

  get media() {
    return this.refs.media;
  }

  get presentation() {
    return this.dataset.presentation;
  }
}

if (!customElements.get('media-gallery')) {
  customElements.define('media-gallery', MediaGallery);
}
